import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';

const API_HEADERS = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const MONTH_OPTIONS = [1, 2, 3, 4, 5, 6, 9, 12, 18, 24, 36, 48, 60];

const PassageRetentionTab = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    mode: 'age',
    ageMonths: 6,
    usedTriggerPercent: 90,
    freeTargetPercent: 20,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/passage-retention', { headers: API_HEADERS() });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Não foi possível carregar');
      setConfig(body.data);
    } catch (error) {
      toast({ title: error.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const freeAtTrigger = 100 - Number(config.usedTriggerPercent);
    if (config.mode === 'space' && Number(config.freeTargetPercent) <= freeAtTrigger) {
      toast({
        title: 'A meta de espaço livre precisa ser maior do que o espaço livre no momento em que a limpeza começa.',
        status: 'warning',
        duration: 5000,
      });
      return;
    }
    try {
      setSaving(true);
      const response = await fetch('/api/passage-retention', {
        method: 'PUT',
        headers: API_HEADERS(),
        body: JSON.stringify(config),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Não foi possível salvar');
      setConfig(body.data);
      toast({ title: 'Reciclagem de passagens salva', status: 'success', duration: 3000 });
    } catch (error) {
      toast({ title: error.message, status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box bg="white" borderRadius="md" boxShadow="sm" p={6} maxW="720px">
      <Heading size="md" mb={2}>Reciclagem de passagens</Heading>
      <Text color="gray.600" mb={4}>
        Remove apenas a foto das passagens. O registro da passagem permanece, e as infrações não são apagadas.
        A limpeza roda em segundo plano, fora do momento em que uma passagem nova é gravada.
      </Text>

      <VStack align="stretch" spacing={5}>
        <FormControl display="flex" alignItems="center">
          <FormLabel mb="0">Ativar reciclagem</FormLabel>
          <Switch
            colorScheme="teal"
            isChecked={config.enabled}
            isDisabled={loading}
            onChange={(event) => setConfig((current) => ({ ...current, enabled: event.target.checked }))}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Critério</FormLabel>
          <RadioGroup
            colorScheme="teal"
            value={config.mode}
            onChange={(mode) => setConfig((current) => ({ ...current, mode }))}
          >
            <Stack spacing={3}>
              <Radio value="age">Por idade da passagem</Radio>
              <Radio value="space">Por espaço livre no disco</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        {config.mode === 'age' ? (
          <FormControl>
            <FormLabel>Apagar fotos mais antigas que</FormLabel>
            <RadioGroup
              colorScheme="teal"
              value={String(config.ageMonths)}
              onChange={(value) => setConfig((current) => ({ ...current, ageMonths: Number(value) }))}
            >
              <Stack direction="row" wrap="wrap" spacing={3}>
                {MONTH_OPTIONS.map((months) => (
                  <Radio key={months} value={String(months)}>
                    {months === 1 ? '1 mês' : months >= 12 && months % 12 === 0 ? `${months / 12} ${months === 12 ? 'ano' : 'anos'}` : `${months} meses`}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
            <FormHelperText>
              A cada alguns minutos o sistema procura fotos de passagem anteriores a esse prazo e apaga os arquivos.
            </FormHelperText>
          </FormControl>
        ) : (
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Começar quando o disco estiver com</FormLabel>
              <NumberInput
                min={50}
                max={99}
                value={config.usedTriggerPercent}
                onChange={(value) => setConfig((current) => ({ ...current, usedTriggerPercent: Number(value) }))}
              >
                <NumberInputField />
              </NumberInput>
              <FormHelperText>Porcentagem usada. Exemplo: 90 começa a limpeza quando restam cerca de 10% livres.</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Liberar até ficar com</FormLabel>
              <NumberInput
                min={5}
                max={50}
                value={config.freeTargetPercent}
                onChange={(value) => setConfig((current) => ({ ...current, freeTargetPercent: Number(value) }))}
              >
                <NumberInputField />
              </NumberInput>
              <FormHelperText>
                Porcentagem livre desejada. Com 90% usado e meta de 20% livre, a limpeza segue até sobrar 20% e só volta quando o disco cair de novo até o limite.
              </FormHelperText>
            </FormControl>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                Só as fotos das passagens mais antigas, no disco que está cheio, entram nessa limpeza.
              </AlertDescription>
            </Alert>
          </Stack>
        )}

        <Button colorScheme="teal" alignSelf="flex-start" onClick={save} isLoading={saving} isDisabled={loading}>
          Salvar
        </Button>
      </VStack>
    </Box>
  );
};

export default PassageRetentionTab;
